// ABOUTME: Manages agent execution display with rationale log and flip card final output.
// ABOUTME: Shows thought process during execution, then flippable results card when complete.

import { useState } from 'react';
import { motion } from 'framer-motion';
import { Wrench, FlipHorizontal2, Pin, PinOff, BookOpen, ChevronUp, ChevronDown } from 'lucide-react';
import type { ContentBlock, ToolResultBlock, ToolUseBlock, TextBlock, SmartAction } from '@/types/sdk';
import { parseAskUserQuestions } from '@/types/sdk';
import { AskUserQuestionCard, AskUserQuestionResolved } from './AskUserQuestionCard';
import { RationaleLog, type RationaleEntry } from './RationaleLog';
import { ContentBlockRenderer } from './ContentBlockRenderer';
import { TextResponseDisplay } from './TextResponseDisplay';
import { PureQueryResultBlock, ExportAllQueriesButton } from './PureQueryResultBlock';
import { parsePureQueryResult, type PureQueryResult } from '@/utils/exportUtils';

// Single learning entry — shows name + truncated detail, click to expand full detail
function LearningEntry({ learning }: { learning: { id: string; name: string; truncatedDetail: string; detail: string } }) {
  const [expanded, setExpanded] = useState(false);
  const hasMore = learning.detail.length > learning.truncatedDetail.length;

  return (
    <div className="text-xs text-surface-500">
      <span className="font-medium text-surface-700">{learning.name}</span>
      {learning.truncatedDetail && (
        <>
          {' — '}
          {expanded ? learning.detail : learning.truncatedDetail}
          {hasMore && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="ml-1 text-primary-500 hover:text-primary-600 cursor-pointer"
            >
              {expanded ? 'less' : 'more'}
            </button>
          )}
        </>
      )}
    </div>
  );
}

// Inline component: collapsed summary of write_learning tool calls
function WriteLearningsSummary({ blocks }: { blocks: ToolUseBlock[] }) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (blocks.length === 0) return null;

  // Extract learning info from each block's input
  const TRUNCATE_LENGTH = 80;
  const learnings = blocks.map((block) => {
    const input = block.input as Record<string, unknown>;
    const data = input.data as Record<string, unknown> | undefined;
    const entryType = (input.learning_type as string) || 'note';
    const name = (data?.display_name as string) || (data?.name as string) || (data?.title as string) || 'Unknown';
    const detail = (data?.description as string) || (data?.content as string) || (data?.notes as string) || '';
    const truncatedDetail = detail.length > TRUNCATE_LENGTH
      ? detail.slice(0, TRUNCATE_LENGTH) + '...'
      : detail;
    return { id: block.id, entryType, name, detail, truncatedDetail };
  });

  const count = learnings.length;
  const summaryText = count === 1
    ? 'Learned 1 new definition'
    : `Learned ${count} new definitions`;

  return (
    <div className="rounded-sm border border-surface-200 bg-surface-50 overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-surface-100 transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-primary-500" />
          <span className="text-sm font-medium text-surface-700">{summaryText}</span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-surface-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-surface-400" />
        )}
      </button>
      {isExpanded && (
        <div className="px-4 pb-3 space-y-2">
          {learnings.map((learning) => (
            <LearningEntry key={learning.id} learning={learning} />
          ))}
        </div>
      )}
    </div>
  );
}

interface AgentExecutionViewProps {
  content: ContentBlock[];
  toolResults: Map<string, ToolResultBlock>;
  isComplete: boolean;
  currentPhase?: string;
  onPin?: () => void;
  onUnpin?: () => void;
  isPinned?: boolean;
  showPinButton?: boolean;
  showSmartActions?: boolean;
  onSmartAction?: (action: SmartAction) => void;
  onSendFollowUp?: (message: string, clarificationAnswer?: boolean) => void;
  /** If a clarification in this message was already resolved, this is the user's selected label. */
  clarificationResolvedLabel?: string;
  /** If an ask_user_question in this message was already answered, this is the user's answer text. */
  askUserResolvedAnswer?: string;
  /** True when the conversation moved past this clarification without answering it (lock the card). */
  askUserSuperseded?: boolean;
}

export function AgentExecutionView({
  content,
  toolResults,
  isComplete,
  currentPhase,
  onPin,
  onUnpin,
  isPinned,
  showPinButton,
  showSmartActions: _showSmartActions,
  onSmartAction: _onSmartAction,
  onSendFollowUp,
  clarificationResolvedLabel,
  askUserResolvedAnswer,
  askUserSuperseded,
}: AgentExecutionViewProps) {
  const [isFlipped, setIsFlipped] = useState(false);

  // Extract rationales from tool use blocks
  const rationaleEntries: RationaleEntry[] = [];
  const toolBlocks: ContentBlock[] = [];
  const pureQueryBlocks: ToolUseBlock[] = []; // execute_pure_query tool calls for primary rendering
  const compiledQueryBlocks: ToolUseBlock[] = []; // compile_pure_query calls render query-only
  const clarificationBlocks: ToolUseBlock[] = []; // resolve_ambiguity tool calls for inline rendering
  const askUserBlocks: ToolUseBlock[] = []; // ask_user_question (SIR clarification) tool calls
  const writeLearningBlocks: ToolUseBlock[] = []; // write_learning tool calls for collapsed summary
  const plainTextBlocks: ContentBlock[] = []; // Regular text (assistant's conversation context)

  // Build a merged toolResults map that includes both passed-in results (from streaming)
  // and results embedded in content blocks (from history loading)
  const mergedToolResults = new Map(toolResults);
  content.forEach((block) => {
    if (block.type === 'tool_result') {
      const resultBlock = block as ToolResultBlock;
      mergedToolResults.set(resultBlock.tool_use_id, resultBlock);
    }
  });

  content.forEach((block, index) => {
    if (block.type === 'tool_use') {
      const toolUseBlock = block as ToolUseBlock;
      const rationale = toolUseBlock.input.rationale as string | undefined;
      // Check if this is the resolve_ambiguity tool (exact match or MCP prefixed)
      const mcpMatch = toolUseBlock.name.match(/^mcp__[^_]+__(.+)$/);
      const normalizedName = mcpMatch ? mcpMatch[1] : toolUseBlock.name;
      const isClarificationTool = normalizedName === 'resolve_ambiguity';
      const isAskUserTool = normalizedName === 'ask_user_question';
      const isWriteLearningTool = normalizedName === 'write_learning';
      const isPureQueryTool = normalizedName === 'execute_pure_query';
      const isCompiledQueryTool = normalizedName === 'compile_pure_query';

      if (rationale) {
        rationaleEntries.push({
          id: toolUseBlock.id,
          toolName: toolUseBlock.name,
          rationale,
          timestamp: Date.now() + index,
        });
      }

      // Route resolve_ambiguity to clarificationBlocks for inline rendering
      if (isClarificationTool) {
        clarificationBlocks.push(toolUseBlock);
      } else if (isAskUserTool) {
        askUserBlocks.push(toolUseBlock);
      } else if (isWriteLearningTool) {
        writeLearningBlocks.push(toolUseBlock);
      } else if (isPureQueryTool) {
        pureQueryBlocks.push(toolUseBlock);
        // Also add to toolBlocks so they appear in technical details
        toolBlocks.push(block);
      } else if (isCompiledQueryTool) {
        compiledQueryBlocks.push(toolUseBlock);
        toolBlocks.push(block);
      } else {
        // Add all other tool calls to toolBlocks for display on back of card
        // Skip SQL/code tool calls only when their primary input is empty
        const hasSqlField = 'sql' in toolUseBlock.input;
        const hasCodeField = 'code' in toolUseBlock.input;
        const isEmptySqlOrCode =
          (hasSqlField && !(toolUseBlock.input.sql as string)?.trim()) ||
          (hasCodeField && !(toolUseBlock.input.code as string)?.trim());

        if (!isEmptySqlOrCode) {
          toolBlocks.push(block);
        }
      }
    } else if (block.type === 'text') {
      // Plain text — assistant's conversation context, shown as fallback
      const textBlock = block as TextBlock;
      if (textBlock.text && textBlock.text.trim().length > 0) {
        plainTextBlocks.push(block);
      }
    }
  });

  // Extract structured PureQueryResult data from tool results
  const pureQueryResults: { block: ToolUseBlock; result: PureQueryResult }[] = [];
  for (const block of pureQueryBlocks) {
    const toolResult = mergedToolResults.get(block.id);
    if (toolResult) {
      const parsed = extractPureQueryResult(toolResult);
      if (parsed) {
        pureQueryResults.push({ block, result: parsed });
      }
    }
  }

  // Collect all Pure query strings for the "Export All" button
  const allPureQueries: string[] = pureQueryResults.map(pqr => pqr.result.pure_query);
  // Also include queries from blocks without results yet (still in tool input)
  for (const block of pureQueryBlocks) {
    const query = block.input.pure_query as string;
    if (query && !allPureQueries.includes(query)) {
      allPureQueries.push(query);
    }
  }

  const hasPureQueryResults = pureQueryResults.length > 0;
  const hasCompiledQueries = compiledQueryBlocks.length > 0;

  // Check if we have plain text content (not empty) - these are assistant's internal notes
  const hasPlainText = plainTextBlocks.length > 0;

  const hasRationales = rationaleEntries.length > 0;
  const hasTechnicalDetails = toolBlocks.length > 0;
  const hasClarifications = clarificationBlocks.length > 0;
  const askUserQuestions = askUserBlocks.length > 0
    ? parseAskUserQuestions(askUserBlocks[0].input)
    : [];
  const hasAskUser = askUserQuestions.length > 0;
  const hasWriteLearnings = writeLearningBlocks.length > 0;

  // Show plain text only when there are no pure query results and no clarifications.
  const shouldShowPlainTextBlocks =
    hasPlainText && !hasClarifications && !hasAskUser && !hasPureQueryResults && !hasCompiledQueries;

  // During execution: show the rationale log (with loading state if no entries yet)
  if (!isComplete) {
    return (
      <RationaleLog
        entries={rationaleEntries}
        isLoading={!hasRationales}
        currentPhase={currentPhase}
        isActive={true}
      />
    );
  }

  // After completion: only show card if we have displayable content
  if (
    !hasPlainText
    && !hasClarifications
    && !hasAskUser
    && !hasWriteLearnings
    && !hasPureQueryResults
    && !hasCompiledQueries
  ) {
    return null;
  }

  // After completion with content: show flippable card
  return (
    <div className="relative" style={{ perspective: '1000px' }} data-tour="insight-card">
      <motion.div
        className="relative w-full"
        initial={false}
        animate={{ rotateY: isFlipped ? 180 : 0 }}
        transition={{ duration: 0.5, ease: 'easeInOut' }}
        style={{ transformStyle: 'preserve-3d' }}
      >
        {/* Front side - Final Output */}
        <div
          className={`w-full card rounded-sm overflow-hidden ${isFlipped ? 'pointer-events-none' : ''}`}
          style={{ backfaceVisibility: 'hidden' }}
        >
          {/* Banner - always shown when there's pinnable content or technical details */}
          {(hasTechnicalDetails || showPinButton) && (
            <div className="sticky top-0 z-20 bg-surface-0 border-b border-surface-200">
              <div className="flex items-center justify-between px-5 py-2.5">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-surface-500">
                    Response
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {hasTechnicalDetails && (
                    <button
                      onClick={() => setIsFlipped(true)}
                      className="flex items-center gap-1.5 px-2 py-1.5 rounded-sm hover:bg-surface-100 text-surface-400 hover:text-surface-700 transition-all cursor-pointer"
                      title="View tool calls"
                    >
                      <Wrench className="w-3.5 h-3.5" />
                      <span className="text-xs">Tool Calls</span>
                    </button>
                  )}
                  {showPinButton && (isPinned ? (
                    <button
                      onClick={onUnpin}
                      className="flex items-center gap-1.5 px-2 py-1.5 rounded-sm bg-primary-50 text-primary-600 hover:bg-primary-100 transition-all cursor-pointer"
                      title="Unpin from dashboard"
                      data-tour="pin-button"
                    >
                      <PinOff className="w-3.5 h-3.5" />
                      <span className="text-xs">Unpin</span>
                    </button>
                  ) : onPin && (
                    <button
                      onClick={onPin}
                      className="flex items-center gap-1.5 px-2 py-1.5 rounded-sm hover:bg-surface-100 text-surface-400 hover:text-surface-700 transition-all cursor-pointer"
                      title="Pin to dashboard"
                      data-tour="pin-button"
                    >
                      <Pin className="w-3.5 h-3.5" />
                      <span className="text-xs">Pin</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Final text response - only show if no structured insight */}
          <div className="p-5 space-y-4">
            {/* Clarification cards — show trace when resolved, interactive card when not */}
            {hasClarifications && clarificationResolvedLabel ? (
              /* Resolved: show the streaming-style trace with a "User selected" final step */
              <RationaleLog
                entries={[
                  ...rationaleEntries,
                  {
                    id: 'user-selection',
                    toolName: 'user_feedback',
                    rationale: clarificationResolvedLabel,
                    timestamp: Date.now(),
                  },
                ]}
                isActive={true}
                isComplete={true}
              />
            ) : (
              /* Clarification cards removed for Goldman POC */
              null
            )}

            {/* SIR clarification (ask_user_question) — interactive until answered, then a summary */}
            {hasAskUser && (
              askUserResolvedAnswer ? (
                <AskUserQuestionResolved questions={askUserQuestions} answer={askUserResolvedAnswer} />
              ) : (
                <AskUserQuestionCard
                  questions={askUserQuestions}
                  onSubmit={(answer) => onSendFollowUp?.(answer, true)}
                  disabled={!onSendFollowUp || askUserSuperseded}
                />
              )
            )}

            {/* Write learning summary — collapsed by default, between clarifications and text */}
            {hasWriteLearnings && <WriteLearningsSummary blocks={writeLearningBlocks} />}

            {/* Pure query results — primary content for Goldman POC */}
            {pureQueryResults.map((pqr, index) => (
              <PureQueryResultBlock
                key={`pure-query-${pqr.block.id}`}
                result={pqr.result}
                queryIndex={pureQueryResults.length > 1 ? index : undefined}
              />
            ))}

            {compiledQueryBlocks.map((block) => (
              <ContentBlockRenderer
                key={`compiled-query-${block.id}`}
                block={block}
                toolResults={mergedToolResults}
              />
            ))}

            {/* Plain text blocks - only shown as fallback when no pure query results or clarifications */}
            {shouldShowPlainTextBlocks && plainTextBlocks.map((block: ContentBlock, index: number) => {
              if (block.type === 'text') {
                const textBlock = block as TextBlock;
                return <TextResponseDisplay key={`plain-text-${index}`} content={textBlock.text} inline />;
              }
              return <ContentBlockRenderer key={`plain-text-${index}`} block={block} />;
            })}

            {/* Export All Queries button — shown when there are multiple queries */}
            {allPureQueries.length > 1 && (
              <ExportAllQueriesButton queries={allPureQueries} />
            )}
          </div>
        </div>

        {/* Back side - Technical Details */}
        <div
          className={`absolute top-0 left-0 w-full h-full card rounded-sm overflow-hidden flex flex-col ${!isFlipped ? 'pointer-events-none' : ''}`}
          style={{
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
          }}
        >
          {/* Fixed header with flip button */}
          <div className="flex-shrink-0 bg-surface-0 border-b border-surface-200">
            <div className="flex items-center justify-between px-5 py-2.5">
              <div className="flex items-center gap-2">
                <Wrench className="w-3.5 h-3.5 text-primary-600" />
                <span className="text-xs font-medium text-surface-600">
                  Technical Details
                </span>
                <span className="text-[10px] text-surface-400">
                  ({toolBlocks.length} {toolBlocks.length === 1 ? 'step' : 'steps'})
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsFlipped(false)}
                  className="flex items-center gap-1.5 px-2 py-1.5 rounded-sm hover:bg-surface-100 text-surface-400 hover:text-surface-700 transition-all cursor-pointer"
                  title="Flip to response"
                >
                  <FlipHorizontal2 className="w-3.5 h-3.5" />
                  <span className="text-xs">Flip</span>
                </button>
                {showPinButton && (isPinned ? (
                  <button
                    onClick={onUnpin}
                    className="flex items-center gap-1.5 px-2 py-1.5 rounded-sm bg-primary-50 text-primary-600 hover:bg-primary-100 transition-colors cursor-pointer"
                    title="Unpin from dashboard"
                    data-tour="pin-button"
                  >
                    <PinOff className="w-3.5 h-3.5" />
                    <span className="text-xs">Unpin</span>
                  </button>
                ) : onPin && (
                  <button
                    onClick={onPin}
                    className="flex items-center gap-1.5 px-2 py-1.5 rounded-sm hover:bg-surface-100 text-surface-400 hover:text-surface-700 transition-colors cursor-pointer"
                    title="Pin to dashboard"
                    data-tour="pin-button"
                  >
                    <Pin className="w-3.5 h-3.5" />
                    <span className="text-xs">Pin</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Scrollable content area */}
          <div className="flex-1 overflow-y-auto p-5 space-y-4">

            {/* Rationale log for context */}
            <RationaleLog
              entries={rationaleEntries}
              isActive={false}
            />

            {/* Full tool call details */}
            <div className="space-y-4 pt-4 border-t border-surface-200">
              {toolBlocks.map((block, index) => (
                <ContentBlockRenderer
                  key={`tool-${index}`}
                  block={block}
                  toolResults={mergedToolResults}
                />
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

/**
 * Extract a PureQueryResult from a tool_result content block.
 * The backend returns two content blocks: text preview + structured JSON.
 * During streaming, content is the raw SDK array. After loading from DB,
 * content may be a string or already-parsed data.
 */
function extractPureQueryResult(toolResult: ToolResultBlock): PureQueryResult | null {
  if (toolResult.is_error) return null;

  const content = toolResult.content;

  // Handle array of content blocks (streaming path)
  if (Array.isArray(content)) {
    for (const item of content) {
      if (typeof item === 'object' && item !== null) {
        const block = item as Record<string, unknown>;
        if (block.type === 'text' && typeof block.text === 'string') {
          const text = (block.text as string).trim();
          if (text.startsWith('{')) {
            try {
              const parsed = JSON.parse(text);
              const result = parsePureQueryResult(parsed);
              if (result) return result;
            } catch {
              // Not valid JSON, continue
            }
          }
        }
        // Check if the item itself is a PureQueryResult (loaded from DB data field)
        const result = parsePureQueryResult(block);
        if (result) return result;
      }
    }
  }

  // Handle string content (may be JSON)
  if (typeof content === 'string') {
    const trimmed = content.trim();
    if (trimmed.startsWith('{')) {
      try {
        const parsed = JSON.parse(trimmed);
        return parsePureQueryResult(parsed);
      } catch {
        // Not valid JSON
      }
    }
  }

  return null;
}
